import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { LogOut, UserCircle2, Menu, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { JournalAttachment, JournalEntry } from "@/types/journal";
import JournalEntryForm from "@/components/journal/JournalEntryForm";
import JournalEntryList from "@/components/journal/JournalEntryList";
import EmotionChart from "@/components/journal/EmotionChart";
import { FeedbackButton } from "@/components/FeedbackButton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { authService } from "@/services/auth.service";
import { Badge } from "@/components/ui/badge";

const Journal = () => {
  const navigate = useNavigate();
  const [entryAttachments, setEntryAttachments] = useState<Record<string, JournalAttachment[]>>({});
  const [user, setUser] = useState<any>(null);
  const [therapist, setTherapist] = useState<{ first_name: string; last_name: string } | null>(null);
  
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (user) {
        // Fetch therapist relationship
        const { data: relationship, error: relError } = await supabase
          .from('client_therapist_relationships')
          .select('therapist_id')
          .eq('client_id', user.id)
          .eq('status', 'active')
          .maybeSingle();
        if (relationship && relationship.therapist_id) {
          // Fetch therapist profile
          const { data: profile, error: profError } = await supabase
            .from('user_profiles')
            .select('first_name, last_name')
            .eq('user_id', relationship.therapist_id)
            .maybeSingle();
          if (profile) {
            setTherapist({ first_name: profile.first_name, last_name: profile.last_name });
          } else {
            setTherapist(null);
          }
        } else {
          setTherapist(null);
        }
      }
    };
    
    getCurrentUser();
  }, []);
  
  const goToProfile = () => {
    navigate('/profile');
  };

  const {
    data: entries,
    refetch,
    isLoading: entriesLoading,
    isError: entriesError,
  } = useQuery({
    queryKey: ['journal-entries'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('journal_entries').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      
      return (data || []).map(entry => ({
        ...entry,
        media_type: entry.media_type === 'audio' || entry.media_type === 'video' 
          ? entry.media_type as 'audio' | 'video' 
          : null
      })) as JournalEntry[];
    },
    retry: false,
  });

  useEffect(() => {
    if (!entries || entries.length === 0) return;
    
    const fetchAttachmentsForEntries = async () => {
      const attachmentsMap: Record<string, JournalAttachment[]> = {};
      
      for (const entry of entries) {
        try {
          const { data, error } = await supabase.rpc('get_journal_attachments', {
            entry_id: entry.id
          });
          
          if (error) {
            console.error(`Error fetching attachments for entry ${entry.id}:`, error);
            continue;
          }
          
          if (data && data.length > 0) {
            attachmentsMap[entry.id] = data as JournalAttachment[];
          }
        } catch (err) {
          console.error(`Failed to fetch attachments for entry ${entry.id}:`, err);
        }
      }
      
      setEntryAttachments(attachmentsMap);
    };
    
    fetchAttachmentsForEntries();
  }, [entries]);

  const handleDeleteSelectedEntries = async (entryIds: string[]) => {
    for (const entryId of entryIds) {
      const attachments = entryAttachments[entryId] ?? [];
      for (const att of attachments) {
        await supabase.storage.from("journal_attachments").remove([att.file_path]);
        await supabase.from("journal_attachments").delete().eq("id", att.id);
      }
      const { error } = await supabase.from("journal_entries").delete().eq("id", entryId);
      if (error) throw error;
    }
    toast.success(`${entryIds.length} ${entryIds.length === 1 ? "entry" : "entries"} deleted`);
    refetch();
  };

  const handleRemoveEntryAttachment = async (entryId: string, attachmentId: string) => {
    try {
      const attachment = entryAttachments[entryId]?.find(a => a.id === attachmentId);
      
      if (!attachment) {
        console.error("Attachment not found");
        return;
      }
      
      const { error: storageError } = await supabase.storage
        .from('journal_attachments')
        .remove([attachment.file_path]);
      
      if (storageError) {
        toast.error(`Error removing file: ${storageError.message}`);
        return;
      }

      const { error: dbError } = await supabase
        .from('journal_attachments')
        .delete()
        .eq('id', attachmentId);
      
      if (dbError) {
        toast.error(`Error deleting attachment record: ${dbError.message}`);
        return;
      }

      toast.success('Attachment deleted successfully');
      
      setEntryAttachments(prev => {
        const updatedEntryAttachments = {...prev};
        
        if (updatedEntryAttachments[entryId]) {
          updatedEntryAttachments[entryId] = updatedEntryAttachments[entryId].filter(
            a => a.id !== attachmentId
          );
        }
        
        return updatedEntryAttachments;
      });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Failed to delete attachment');
    }
  };

  const handleLogout = async () => {
    try {
      const { success } = await authService.signOut();
      
      if (success) {
        // Clear any stored data
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
        localStorage.removeItem('userId');
        localStorage.removeItem('clientsInfo');
        localStorage.removeItem('isAdmin');
        localStorage.removeItem('redirectAfterLogin');
        
        toast.success("Logged out successfully");
        navigate('/auth');
      } else {
        toast.error("Failed to log out");
        navigate('/auth');
      }
    } catch (error) {
      console.error("Error logging out:", error);
      toast.error("Logout failed. Please try again.");
      navigate('/auth');
    }
  };

  const chartData = entries?.map((entry) => {
    const hasEmotion = entry.emotion_type != null && String(entry.emotion_type).trim() !== "";
    const hasIntensity = entry.emotion_intensity != null && Number.isFinite(entry.emotion_intensity);
    if (hasEmotion && hasIntensity) {
      return {
        date: new Date(entry.created_at).toLocaleDateString(),
        intensity: entry.emotion_intensity,
        emotion: entry.emotion_type,
        timestamp: entry.created_at
      };
    }
    return null;
  })
  .filter(Boolean)
  .reverse() || [];

  if (entriesError) {
    return (
      <div className="relative min-h-screen w-full">
        <div className="fixed inset-0 w-full h-full bg-gradient-to-b from-[#E8F0F8] to-white z-0" />
        <div className="relative z-10 min-h-screen w-full flex items-center justify-center">
          <div className="text-center px-4">
            <p className="text-[#333B44] font-medium mb-2">Unable to load your journal.</p>
            <p className="text-sm text-[#5A636E] mb-4">Your session may have expired. Please try again or log in.</p>
            <Button onClick={() => refetch()} variant="outline" className="mr-2">Try again</Button>
            <Button onClick={() => navigate("/auth")} variant="outline">Go to login</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full">
      {/* Full-screen blue gradient background */}
      <div className="fixed inset-0 w-full h-full bg-gradient-to-b from-[#E8F0F8] to-white z-0" />
      <div className="relative z-10 min-h-screen w-full">
        <img src="https://app.clamalo.com/images/1.png" alt="Clou Background" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-10 w-[60vw] max-w-[600px] pointer-events-none select-none z-0" />
        <div className="container mx-auto px-4 py-8 relative z-10">
          <header className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <img src="/clou_logo.png" alt="Clou Logo" className="max-h-10 w-auto object-contain" />
            </div>
            {/* Desktop: show all buttons */}
            <div className="hidden sm:flex items-center gap-2">
              <Button 
                onClick={goToProfile} 
                variant="outline" 
                className="flex items-center gap-2 border-[#CCCCCC] hover:bg-[#E8F0F8]"
              >
                <UserCircle2 className="h-4 w-4" />
                My Profile
              </Button>
              <Button 
                variant="outline" 
                className="flex items-center gap-2 border-[#CCCCCC] hover:bg-[#E8F0F8]"
                onClick={() => navigate('/therapists')}
              >
                <Users className="h-4 w-4" />
                Therapist Directory
              </Button>
              <FeedbackButton />
              <Button 
                variant="outline" 
                onClick={handleLogout} 
                className="flex items-center gap-2 border-[#CCCCCC] hover:bg-[#E8F0F8]"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </div>
            {/* Mobile: show dropdown menu */}
            <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="border-[#CCCCCC]">
                    <Menu className="h-6 w-6" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={goToProfile}>
                    <UserCircle2 className="h-4 w-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/therapists')}>
                    <Users className="h-4 w-4 mr-2" /> Therapist Directory
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <FeedbackButton />
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="h-4 w-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {entriesLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-[#3BAFDA]" />
              <span className="ml-2 text-[#5A636E]">Loading journal...</span>
            </div>
          ) : (
          <>
          {/* Therapist Connection Indicator */}
          <div className="mb-6">
            {therapist ? (
              <Badge className="bg-green-100 text-green-800 border-green-200 px-4 py-2 text-base font-medium hover:bg-green-100 focus:bg-green-100 active:bg-green-100">
                Connected to Therapist, {therapist.first_name} {therapist.last_name}
              </Badge>
            ) : (
              <Link to="/therapists">
                <Badge className="cursor-pointer bg-[#E8F0F8] text-[#333B44] border-[#CCCCCC] px-4 py-2 text-base font-medium hover:bg-[#E8F0F8]/80 focus:bg-[#E8F0F8] active:bg-[#E8F0F8] transition-colors">
                  Not connected to a Therapist
                </Badge>
              </Link>
            )}
          </div>
          <JournalEntryForm onEntryCreated={refetch} />
          <EmotionChart chartData={chartData} />
          <div className="flex justify-end mb-4">
            <Button variant="outline" onClick={() => navigate('/journal/all')}>
              View All Journal Entries
            </Button>
          </div>
          <JournalEntryList 
            entries={entries}
            entryAttachments={entryAttachments}
            onAttachmentDelete={handleRemoveEntryAttachment}
            showAll={false}
            deletable={true}
            onDeleteSelected={handleDeleteSelectedEntries}
          />
          </>
          )}
        </div>
      </div>
      <div style={{ position: 'fixed', bottom: 12, right: 16, zIndex: 50 }} className="text-xs text-[#5A636E] select-none pointer-events-none">
        V1.0.001
      </div>
    </div>
  );
};

export default Journal;
